/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Directive, EventEmitter, Inject, Input, OnChanges, Optional, Output, Self, SimpleChanges, forwardRef} from '@angular/core';
import {FormArray, FormControl, FormGroup} from '../../model';
import {NG_ASYNC_VALIDATORS, NG_VALIDATORS, Validators} from '../../validators';
import {ControlContainer} from '../control_container';
import {Form} from '../form_interface';
import {ReactiveErrors} from '../reactive_errors';
import {cleanUpControl, composeAsyncValidators, composeValidators, setUpControl, setUpFormContainer} from '../shared';

import {FormControlName} from './form_control_name';
import {FormArrayName, FormGroupName} from './form_group_name';

export const /** @type {?} */ formDirectiveProvider: any = {
  provide: ControlContainer,
  useExisting: forwardRef(() => FormGroupDirective)
};
/**
 * \@whatItDoes Binds an existing {\@link FormGroup} to a DOM element.
 * 
 * \@howToUse 
 * 
 * This directive accepts an existing {\@link FormGroup} instance. It will then use this
 * {\@link FormGroup} instance to match any child {\@link FormControl}, {\@link FormGroup},
 * and {\@link FormArray} instances to child {\@link FormControlName}, {\@link FormGroupName},
 * and {\@link FormArrayName} directives.
 * 
 * **Set value**: You can set the form's initial value when instantiating the
 * {\@link FormGroup}, or you can set it programmatically later using the {\@link FormGroup}'s
 * {\@link AbstractControl#setValue} or {\@link AbstractControl#patchValue} methods.
 * 
 * **Listen to value**: If you want to listen to changes in the value of the form, you can subscribe
 * to the {\@link FormGroup}'s {\@link AbstractControl#valueChanges} event.  You can also listen to
 * its {\@link AbstractControl#statusChanges} event to be notified when the validation status is
 * re-calculated.
 * 
 * Furthermore, you can listen to the directive's `ngSubmit` event to be notified when the user has
 * triggered a form submission. The `ngSubmit` event will be emitted with the original form
 * submission event.
 * 
 * ### Example
 * 
 * In this example, we create form controls for first name and last name.
 * 
 * {\@example forms/ts/simpleFormGroup/simple_form_group_example.ts region='Component'}
 * 
 * **npm package**: `\@angular/forms`
 * 
 * **NgModule**: {\@link ReactiveFormsModule}
 * 
 *  \@stable
 */
export class FormGroupDirective extends ControlContainer implements Form,
    OnChanges {
private _submitted: boolean = false;
private _oldForm: FormGroup;
  directives: FormControlName[] = [];

   form: FormGroup = /** @type {?} */(( null));
   ngSubmit = new EventEmitter();
/**
 * @param {?} _validators
 * @param {?} _asyncValidators
 */
constructor(
private _validators: any[],
private _asyncValidators: any[]) {
    super();
  }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges): void {
    this._checkFormPresent();
    if (changes.hasOwnProperty('form')) {
      this._updateValidators();
      this._updateDomValue();
      this._updateRegistrations();
    }
  }
/**
 * @return {?}
 */
get submitted(): boolean { return this._submitted; }
/**
 * @return {?}
 */
get formDirective(): Form { return this; }
/**
 * @return {?}
 */
get control(): FormGroup { return this.form; }
/**
 * @return {?}
 */
get path(): string[] { return []; }
/**
 * @param {?} dir
 * @return {?}
 */
addControl(dir: FormControlName): FormControl {
    const /** @type {?} */ ctrl: any = this.form.get(dir.path);
    setUpControl(ctrl, dir);
    ctrl.updateValueAndValidity({emitEvent: false});
    this.directives.push(dir);
    return ctrl;
  }
/**
 * @param {?} dir
 * @return {?}
 */
getControl(dir: FormControlName): FormControl { return /** @type {?} */(( <FormControl>this.form.get(dir.path))); }
/**
 * @param {?} dir
 * @return {?}
 */
removeControl(dir: FormControlName): void { remove(this.directives, dir); }
/**
 * @param {?} dir
 * @return {?}
 */
addFormGroup(dir: FormGroupName): void {
    const /** @type {?} */ ctrl: any = this.form.get(dir.path);
    setUpFormContainer(ctrl, dir);
    ctrl.updateValueAndValidity({emitEvent: false});
  }
/**
 * @param {?} dir
 * @return {?}
 */
removeFormGroup(dir: FormGroupName): void {}
/**
 * @param {?} dir
 * @return {?}
 */
getFormGroup(dir: FormGroupName): FormGroup { return /** @type {?} */(( <FormGroup>this.form.get(dir.path))); }
/**
 * @param {?} dir
 * @return {?}
 */
addFormArray(dir: FormArrayName): void {
    const /** @type {?} */ ctrl: any = this.form.get(dir.path);
    setUpFormContainer(ctrl, dir);
    ctrl.updateValueAndValidity({emitEvent: false});
  }
/**
 * @param {?} dir
 * @return {?}
 */
removeFormArray(dir: FormArrayName): void {}
/**
 * @param {?} dir
 * @return {?}
 */
getFormArray(dir: FormArrayName): FormArray { return /** @type {?} */(( <FormArray>this.form.get(dir.path))); }
/**
 * @param {?} dir
 * @param {?} value
 * @return {?}
 */
updateModel(dir: FormControlName, value: any): void {
    const /** @type {?} */ ctrl  = /** @type {?} */(( <FormControl>this.form.get(dir.path)));
    ctrl.setValue(value);
  }
/**
 * @param {?} $event
 * @return {?}
 */
onSubmit($event: Event): boolean {
    this._submitted = true;
    this.ngSubmit.emit($event);
    return false;
  }
/**
 * @return {?}
 */
onReset(): void { this.resetForm(); }
/**
 * @param {?=} value
 * @return {?}
 */
resetForm(value: any = undefined): void {
    this.form.reset(value);
    this._submitted = false;
  }
/**
 * \@internal
 * @return {?}
 */
_updateDomValue() {
    this.directives.forEach(dir => {
      const /** @type {?} */ newCtrl: any = this.form.get(dir.path);
      if (dir._control !== newCtrl) {
        cleanUpControl(dir._control, dir);
        if (newCtrl) setUpControl(newCtrl, dir);
        dir._control = newCtrl;
      }
    });

    this.form._updateTreeValidity({emitEvent: false});
  }
/**
 * @return {?}
 */
private _updateRegistrations() {
    this.form._registerOnCollectionChange(() => this._updateDomValue());
    if (this._oldForm) this._oldForm._registerOnCollectionChange(() => {});
    this._oldForm = this.form;
  }
/**
 * @return {?}
 */
private _updateValidators() {
    const /** @type {?} */ sync = composeValidators(this._validators);
    this.form.validator = Validators.compose([ /** @type {?} */((this.form.validator)), /** @type {?} */(( sync))]);

    const /** @type {?} */ async = composeAsyncValidators(this._asyncValidators);
    this.form.asyncValidator = Validators.composeAsync([ /** @type {?} */((this.form.asyncValidator)), /** @type {?} */(( async))]);
  }
/**
 * @return {?}
 */
private _checkFormPresent() {
    if (!this.form) {
      ReactiveErrors.missingFormException();
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[formGroup]',
  providers: [formDirectiveProvider],
  host: {'(submit)': 'onSubmit($event)', '(reset)': 'onReset()'},
  exportAs: 'ngForm'
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Array, decorators: [{ type: Optional }, { type: Self }, { type: Inject, args: [NG_VALIDATORS, ] }, ]},
{type: Array, decorators: [{ type: Optional }, { type: Self }, { type: Inject, args: [NG_ASYNC_VALIDATORS, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'form': [{ type: Input, args: ['formGroup', ] },],
'ngSubmit': [{ type: Output },],
};
}

function FormGroupDirective_tsickle_Closure_declarations() {
/** @type {?} */
FormGroupDirective.decorators;
/**
 * @nocollapse
 * @type {?}
 */
FormGroupDirective.ctorParameters;
/** @type {?} */
FormGroupDirective.propDecorators;
/** @type {?} */
FormGroupDirective.prototype._submitted;
/** @type {?} */
FormGroupDirective.prototype._oldForm;
/** @type {?} */
FormGroupDirective.prototype.directives;
/** @type {?} */
FormGroupDirective.prototype.form;
/** @type {?} */
FormGroupDirective.prototype.ngSubmit;
/** @type {?} */
FormGroupDirective.prototype._validators;
/** @type {?} */
FormGroupDirective.prototype._asyncValidators;
}

/**
 * @template T
 * @param {?} list
 * @param {?} el
 * @return {?}
 */
function remove<T>(list: T[], el: T): void {
  const /** @type {?} */ index = list.indexOf(el);
  if (index > -1) {
    list.splice(index, 1);
  }
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
